import { css, html, LitElement } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import { customElement, property, query, state } from 'lit/decorators.js';
import './components/Button';
import './components/FileCard';
import { closeIcon } from './shared/icons/close';
import { LocalFile } from './types';
import { uniqueId } from './utils';

@customElement('media-picker-upload-file')
export class MediaPickerUploadFile extends LitElement {
  static readonly styles = css`
    :host {
      font-family: Roboto, sans-serif;
      --media-picker-primary-color: #3f51b5;
      --media-picker-on-primary-color: #fff;
      --media-picker-spacing-vertical: 12px;
      --media-picker-spacing-horizontal: 24px;
      font-family: Roboto, sans-serif;
    }

    * {
      box-sizing: border-box;
    }

    dialog {
      position: fixed;
      display: flex;
      flex-direction: column;
      inset: 0;
      transition: all 0.5s;
      padding: 0;
      box-shadow: 0 0 2rem 0 rgba(0, 0, 0, 0.5);
      background-color: #f7f9ff;
      color: #000;
      width: 100%;
      height: 100%;
      max-width: 90%;
      max-height: 90%;
      border: none;
      transform: scale(0.9);
      border-radius: 12px;
      overflow: hidden;
      z-index: 2;
    }

    dialog[open] {
      animation: 0.5s cubic-bezier(0.35, 0.25, 0, 0.72) 0s 1 normal forwards
        running show-dialog;
    }

    dialog:not([open]) {
      display: none;
      pointer-events: none;
      opacity: 0;
    }

    dialog::backdrop {
      background-color: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(1px);
    }

    dialog header {
      margin: 0;
      padding: 0 24px;
      color: var(--media-picker-on-primary-color);
      background-color: var(--media-picker-primary-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 64px;
    }

    dialog header h3 {
      margin: 0;
      margin-block-start: 0;
      margin-block-end: 0;
    }

    dialog header .dialog-header-actions {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
    }

    dialog .dialog-content {
      flex: 1;
      overflow: auto;
    }

    dialog footer {
      height: 64px;
      padding: 12px 24px;
      display: flex;
      align-items: center;
      gap: 24px;
      border-top: 1px solid #e5e7eb;
      background-color: #f7f9ff;
    }

    dialog .dialog-content .form {
      display: flex;
      flex-direction: row;
      gap: 24px;
      flex-direction: row;
    }

    dialog .dialog-content .form .file-preview {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    dialog .dialog-content .form .file-details {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .dialog-close {
      position: absolute;
      top: 12px;
      right: 12px;
      cursor: pointer;
      z-index: 3;
    }

    .flex-1 {
      flex: 1;
    }

    .drop-zone {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      width: 100%;
    }

    .drop-zone .drop-zone__label {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border: 2px dashed grey;
      border-radius: 12px;
      padding: 64px;
      transition: all 0.5s ease-in-out;
    }

    .drop-zone .drop-zone__label:hover {
      color: var(--media-picker-primary-color);
      border-color: var(--media-picker-primary-color);
      border-width: 2px;
    }

    .drop-zone .drop-zone__label .drop-zone__label__wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      cursor: pointer;
    }

    .drop-zone .drop-zone__label svg {
      height: 64px;
      width: 64px;
      margin-bottom: 16px;
      color: grey;
      animation: jump 2s infinite cubic-bezier(0.46, -0.08, 0.45, 0.9);
      cursor: pointer;
      transition: all 0.5s ease-in-out;
    }

    .drop-zone .drop-zone__label:hover svg {
      color: var(--media-picker-primary-color);
    }

    .drop-zone .drop-zone__label .drop-zone__label__wrapper__p {
      margin-block-start: 0;
      margin-block-end: 0;
      margin-bottom: 8px;
      cursor: pointer;
    }

    .drop-zone .drop-zone__label .drop-zone__label__wrapper__p__span {
      font-weight: bold;
      cursor: pointer;
    }

    .drop-zone .drop-zone__input {
      display: none;
    }

    .image-editor {
      height: 100%;
      width: 100%;
    }

    .image-editor cropper-canvas {
      height: 100%;
      width: 100%;
    }

    .editor-toolbar {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      position: absolute;
      bottom: 12px;
      width: 100%;
      gap: 12px;
      transition: all 0.2s ease-in-out;
      transform: translateY(100px);
    }

    .editor-toolbar--edit {
      transform: translateY(0);
    }

    .editor-toolbar .editor-toolbar__button {
      cursor: pointer;
      width: 48px;
      height: 48px;

      border-radius: 8px;
      border: 1px solid var(--media-picker-primary-color);
      color: var(--media-picker-primary-color);
      font-size: 14px;
      transition: all 0.2s ease-in-out;
    }

    @keyframes show-dialog {
      100% {
        transform: scale(1);
      }
    }

    @keyframes jump {
      0% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-8px);
      }
      100% {
        transform: translateY(0);
      }
    }

    .files {
      box-sizing: border-box;
      margin-left: var(--spacing-horizontal);
      margin-right: var(--spacing-horizontal);
      margin-top: var(--spacing-vertical);
      margin-bottom: var(--spacing-vertical);

      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: var(--spacing-horizontal);
    }

    .files media-file-card {
      grid-column: span 3;
    }

    .button {
      min-width: 128px;
    }
  `;

  // **************************************************************************
  // *** UI properties
  // **************************************************************************

  @property({ type: String })
  text: string = `Click to upload`;

  @property({ type: String })
  subtitle: string = `SVG, PNG, JPG or GIF (MAX. 800x400px)`;

  // **************************************************************************
  // *** File picker properties
  // **************************************************************************

  /**
   * The accept attribute specifies what file types the server accepts (which
   * can be different from the accept attribute of the <input> element).
   */
  @property({ type: String }) accept: string = 'image/*';

  /**
   * The maxFiles attribute specifies the maximum number of files that can be
   * selected.
   */
  @property({ type: Number }) maxFiles: number = 1;

  /**
   * The maxFileSize attribute specifies the maximum file size in bytes.
   */
  @property({ type: Number }) maxFileSize: number = 800;

  // **************************************************************************
  // *** Api properties
  // **************************************************************************
  /**
   * The folderPath attribute specifies the path to the folder where the file
   */
  @property({ type: String }) folderPath: string = '';

  // **************************************************************************
  // *** State
  // **************************************************************************
  @state() private mode: 'pick' | 'upload' | 'select' = 'pick';

  /**
   * The uploading state
   */
  @state() private _uploading: boolean = false;

  @state() private files: LocalFile[] = [];

  @query('dialog') _dialog!: HTMLDialogElement;

  /**
   * Closes the dialog
   */
  private readonly closeDialog = () => {
    this._dialog.close();
    this._clearFiles();
    this._switchModeTo('pick');
  };

  private _switchModeTo(newMode: 'pick' | 'upload' | 'select') {
    this.mode = newMode;
  }

  /**
   * Clear files array
   */
  private _clearFiles() {
    this.files = [];
  }

  /**
   * Add files to files array
   *
   * @param files The files to add.
   */
  private _addFiles(files: LocalFile[]) {
    this.files = [...this.files, ...files];
  }

  /**
   * Remove file from files array
   *
   * @param file The file to remove.
   */
  private _removeFile(file: LocalFile) {
    this.files = this.files.filter(f => f.id !== file.id);

    if (this.files.length === 0) {
      this._switchModeTo('pick');
    }
  }

  /**
   * File change
   *
   * Handles file change events and dispatches a custom event with the selected files.
   * @param event The file change event.
   */
  private readonly _fileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const inputFiles = target.files;
    if (!inputFiles) return;

    const filePromises = Array.from(inputFiles).map(
      file =>
        new Promise<LocalFile>(resolve => {
          const commonData = {
            id: uniqueId(),
            name: file.name,
            size: file.size / 1024, // by default size is in kb
            type: file.type,
            file,
          };

          // in case of image file then create image object and get width and height
          // else resolve common data
          if (file.type.startsWith('image/')) {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
              resolve({
                ...commonData,
                previewUrl: url,
                width: img.width,
                height: img.height,
              });
              // It's good practice to revoke the object URL when it's no longer needed
              // but since we use it for the preview, we might revoke it on component disconnect.
            };
            img.onerror = () => {
              // Handle cases where the file is not a valid image
              resolve({ ...commonData, previewUrl: undefined });
              URL.revokeObjectURL(url);
            };
            img.src = url;
          } else {
            // Not an image, resolve immediately
            resolve(commonData);
          }
        })
    );

    // updated files array and switch to upload mode
    this._addFiles(await Promise.all(filePromises));
    this._switchModeTo('upload');
  };

  /**
   * Upload files to api
   */
  private _uploadFilesToApi() {
    const baseurl = window.location.origin;
    let url = `${baseurl}/cms/api/upload`;

    const formData = new FormData();
    this.files.forEach(item => {
      formData.append('files', item.file);
    });
    formData.append('path', this.folderPath);

    // Send the formData to the server
    fetch(url, {
      method: 'POST',
      body: formData,
    })
      .then(response => response.json())
      .then(data => {
        // close dialog, clear files array, switch to pick mode
        this.closeUploadFileDialog();
        this._clearFiles();
        this._switchModeTo('pick');

        this.dispatchEvent(
          new CustomEvent('files-uploaded', {
            detail: {
              files: data,
            },
          })
        );
      })
      .finally(() => {});
  }

  /**
   * Open upload file dialog
   *
   * This method opens the upload file dialog.
   */
  openUploadFileDialog() {
    this._dialog.showModal();
  }

  /**
   * Close upload file dialog
   *
   * This method closes the upload file dialog.
   */
  closeUploadFileDialog() {
    this._dialog.close();
  }

  renderBody() {
    if (this.mode === 'upload' && this.files.length > 0) {
      return html`
        <div class="files">
          ${this.files.map(
            file => html`
              <media-file-card
                .fileName=${file.name}
                .fileSize=${file.size}
                .fileType=${file.type}
                .width=${file.width}
                .height=${file.height}
                .previewUrl=${file.previewUrl}
                .selectable=${false}
                .editable=${false}
                .removable=${true}
                @remove=${this._removeFile.bind(this, file)}
              />
            `
          )}
        </div>
      `;
    }

    return html`
      <div class="drop-zone">
        <label for="dropzone-file" class="drop-zone__label">
          <div class="drop-zone__label__wrapper">
            <svg
              class="drop-zone__label__wrapper__svg"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
              />
            </svg>
            <p class="drop-zone__label__wrapper__p">
              <span class="drop-zone__label__wrapper__p__span"
                >Click to upload</span
              >
              or drag and drop
            </p>
            <p class="drop-zone__label__wrapper__p">
              SVG, PNG, JPG or GIF (MAX. 800x400px)
            </p>
          </div>
          <input
            id="dropzone-file"
            type="file"
            class="drop-zone__input"
            accept=${this.accept}
            multiple=${this.maxFiles > 1}
            @change="${this._fileChange.bind(this)}"
          />
        </label>
      </div>
    `;
  }

  renderActions() {
    if (this.mode === 'upload') {
      return html`
        <media-picker-button
          label="Cancel"
          variant="danger"
          @click="${this.closeDialog}"
        ></media-picker-button>

        <div class="flex-1"></div>

        <media-picker-button
          label="Add more files"
          @click="${this._switchModeTo.bind(this, 'pick')}"
        ></media-picker-button>

        <media-picker-button
          variant="primary"
          label="Upload selected files"
          @click="${this._uploadFilesToApi.bind(this)}"
        ></media-picker-button>
      `;
    }

    if (this.mode === 'select') {
      return html`
        <media-picker-button
          variant="secondary"
          label="Cancel"
          @click="${this.closeDialog}"
        ></media-picker-button>
        <media-picker-button
          variant="primary"
          label="Upload"
          @click="${this._uploadFilesToApi.bind(this)}"
        ></media-picker-button>
      `;
    }

    return html``;
  }

  render() {
    return html`
      <dialog>
        <header>
          <h3>Upload File</h3>
          <div class="dialog-close">
            <icon-button
              .icon=${closeIcon}
              @click="${this.closeDialog}"
            ></icon-button>
          </div>
        </header>
        <div class="dialog-content">${this.renderBody()}</div>
        <footer
          class=${classMap({
            footer: true,
            'footer--show': this.files.length > 0,
          })}
        >
          ${this.renderActions()}
        </footer>
      </dialog>
    `;
  }
}

@customElement('image-editor')
class ImageEditor extends LitElement {
  @state() _cropper: any;

  override render() {
    return html``;
  }
}
